import { useEffect, useRef, useState, type JSX } from 'react';
import {
  charactersApi,
  isDeadlocked,
  renditionFor,
  CHARACTER_KINDS,
  type CharacterDto,
  type CharacterKind,
  type CharacterPhotoSlot,
  type GenerationWarning,
} from '../api/library.js';
import { NumberField, SelectField, Section, TextArea, TextField } from '../components/Fields.js';
import { useAuth } from '../auth/context.js';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';
import { PhotoAdvice } from './PhotoAdvice.js';
import { CharacterPhotoSlots } from './PhotoSlots.js';
import { RenditionPanel } from './RenditionPanel.js';

interface Draft {
  name: string;
  displayName: string;
  kind: CharacterKind;
  role: string;
  ageYears: number | null;
  gender: CharacterGenderValue;
  traits: string;
  favouriteActivities: string;
  catchphrase: string;
}

type CharacterGenderValue = '' | 'male' | 'female';
type SaveState = 'idle' | 'saving' | 'saved';

/**
 * Карточка героя (01-SPEC.md §5.5): анкета, три фото по слотам
 * и лента вариантов. Одна и та же карточка работает и в библиотеке,
 * и в шаге конструктора — во втором случае рендер привязан к стилю книги.
 */
export function CharacterEditor({
  character,
  styleId,
  bookId,
  readOnly = false,
  onChanged,
}: {
  character: CharacterDto;
  styleId: string | null;
  bookId?: string;
  readOnly?: boolean;
  onChanged(next: CharacterDto): void;
}): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(character));
  const [pending, setPending] = useState(false);
  const [warnings, setWarnings] = useState<readonly GenerationWarning[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const photosRef = useRef<HTMLDivElement | null>(null);
  // Approval deletes the photos in the background; until a refetch they still came back,
  // with Replace and Remove under them. Once this book's drawing is approved there is
  // nothing to upload, unless the parent asks to redraw from new photos.
  const [uploadingAgain, setUploadingAgain] = useState(false);

  useEffect(() => {
    setDraft(draftFrom(character));
  }, [character]);

  const rendition = styleId ? renditionFor(character, styleId) : undefined;
  const patch = (value: Partial<Draft>): void => {
    setSaveState('idle');
    setDraft((current) => ({ ...current, ...value }));
  };
  const run = (
    action: Promise<CharacterDto>,
    onSuccess?: () => void,
    onFailure?: () => void,
  ): Promise<void> => {
    setPending(true);
    setError(null);
    return action
      .then((next) => {
        onChanged(next);
        onSuccess?.();
      })
      .catch((reason: unknown) => {
        setError(userErrorMessage(reason, t));
        onFailure?.();
      })
      .finally(() => setPending(false));
  };
  const save = (): Promise<void> => {
    setSaveState('saving');
    return run(
      charactersApi.update(character.id, toInput(draft)),
      () => setSaveState('saved'),
      () => setSaveState('idle'),
    );
  };

  const generate = (): void => {
    if (!styleId) return;
    // Drawing a person without a photo spends an attempt on a stranger's face; the warning
    // used to arrive only after the attempt was spent. A pet can be drawn from a description.
    // After approval the photos are deleted, and a redraw starts from nothing too — the
    // approved drawing is not a reference — so it says what the parent is about to lose.
    if (
      character.kind !== 'pet' &&
      character.photos.length === 0 &&
      !window.confirm(
        // Only a drawing made from photos had photos to delete; one drawn without them
        // gets the ordinary warning.
        rendition?.status === 'approved' && rendition.lastRefAssetIds.length > 0
          ? t.library.rendition.redrawWithoutPhotosConfirm
          : t.library.rendition.drawWithoutPhotosConfirm,
      )
    ) {
      return;
    }
    setPending(true);
    setError(null);
    void charactersApi
      .ensureRendition(character.id, styleId)
      .then((withRendition) => {
        const target = renditionFor(withRendition, styleId);
        if (!target) return withRendition;
        return charactersApi.generate(target.id, bookId).then((accepted) => {
          setWarnings(accepted.warnings);
          return charactersApi.get(character.id);
        });
      })
      .then(onChanged)
      .catch((reason: unknown) => setError(userErrorMessage(reason, t)))
      .finally(() => setPending(false));
  };

  return (
    <div className="grid gap-6">
      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {/*
        What drawing and story need comes first: name, type, age, personality. The rest shapes
        the story but is optional, and nine fields at once read as a form to get through
        before anything happens.
      */}
      <fieldset disabled={readOnly} className="grid items-start gap-5 sm:grid-cols-2">
        <TextField
          label={t.characters.fields.name}
          value={draft.name}
          onChange={(name) => patch({ name })}
        />
        <SelectField
          label={t.characters.fields.kind}
          value={draft.kind}
          options={CHARACTER_KINDS.map((kind) => ({
            value: kind,
            label: t.characters.kinds[kind],
          }))}
          onChange={(kind) => patch({ kind })}
        />
        <NumberField
          label={t.characters.fields.ageYears}
          value={draft.ageYears}
          min={0}
          max={120}
          onChange={(ageYears) => patch({ ageYears })}
        />
        <SelectField<CharacterGenderValue>
          label={t.characters.fields.gender}
          value={draft.gender}
          options={[
            { value: '', label: t.characters.genders.unspecified },
            { value: 'male', label: t.characters.genders.male },
            { value: 'female', label: t.characters.genders.female },
          ]}
          onChange={(gender) => patch({ gender })}
        />
        <TextArea
          label={t.characters.fields.traits}
          hint={t.characters.fields.traitsHint}
          value={draft.traits}
          onChange={(traits) => patch({ traits })}
        />
      </fieldset>

      <details className="kitup-details" open={hasOptionalDetails(draft)}>
        <summary className="cursor-pointer text-sm font-semibold">
          {t.characters.moreDetails}
        </summary>
        <fieldset disabled={readOnly} className="mt-4 grid items-start gap-5 sm:grid-cols-2">
          <TextField
            label={t.characters.fields.displayName}
            hint={t.characters.fields.displayNameHint}
            value={draft.displayName}
            onChange={(displayName) => patch({ displayName })}
          />
          <TextField
            label={t.characters.fields.role}
            hint={t.characters.fields.roleHint}
            value={draft.role}
            onChange={(role) => patch({ role })}
          />
          <TextArea
            label={t.characters.fields.favouriteActivities}
            hint={t.characters.fields.traitsHint}
            value={draft.favouriteActivities}
            onChange={(favouriteActivities) => patch({ favouriteActivities })}
          />
          <TextField
            label={t.characters.fields.catchphrase}
            hint={t.characters.fields.catchphraseHint}
            value={draft.catchphrase}
            onChange={(catchphrase) => patch({ catchphrase })}
          />
        </fieldset>
      </details>

      {readOnly ? null : (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="kitup-secondary-button"
            disabled={pending}
            onClick={() => void save()}
          >
            {saveState === 'saving' ? t.common.saving : t.library.actions.save}
          </button>
          {saveState === 'saved' ? (
            <span role="status" className="text-sm text-emerald-700">
              {t.common.saved}
            </span>
          ) : null}
        </div>
      )}

      <Section title={t.library.photos.title}>
        {/*
          Approving a drawing deletes the photos it was drawn from (docs/photo-retention.md),
          and the empty slots then read as if nothing had ever been uploaded. Say what
          happened, and what it means for the drawing this book needs.
        */}
        {(character.photos.length === 0 &&
          character.renditions.some((item) => item.status === 'approved')) ||
        (rendition?.status === 'approved' && !uploadingAgain && !readOnly) ? (
          <p
            role="status"
            className="mb-3 rounded-[var(--kitup-radius-sm)] bg-emerald-50 p-3 text-sm text-emerald-900"
          >
            {/* The library has no book, so no style to need a new drawing for. */}
            {bookId && rendition?.status !== 'approved'
              ? t.library.photos.deletedNeedNewStyle
              : t.library.photos.deletedAfterApproval}
          </p>
        ) : null}
        {rendition?.status === 'approved' && !uploadingAgain && !readOnly ? (
          <button
            type="button"
            className="kitup-secondary-button w-fit"
            onClick={() => setUploadingAgain(true)}
          >
            {t.library.photos.uploadAgain}
          </button>
        ) : null}
        {/* Read-only (story approved), the references stay visible to look at. */}
        <div
          ref={photosRef}
          hidden={rendition?.status === 'approved' && !uploadingAgain && !readOnly}
        >
          <CharacterPhotoSlots
            photos={character.photos}
            issues={character.photoReview.issues}
            disabled={!(user?.consentPhotoProcessing ?? false)}
            readOnly={readOnly}
            onChange={(photos) =>
              run(
                charactersApi.setPhotos(
                  character.id,
                  photos as readonly { assetId: string; slot: CharacterPhotoSlot; order: number }[],
                ),
              )
            }
          />
        </div>
      </Section>

      <Section title={t.library.rendition.variantsTitle}>
        <RenditionPanel
          rendition={rendition}
          styleReady={Boolean(styleId)}
          pending={pending}
          warnings={warnings}
          readOnly={readOnly}
          deadlock={
            rendition && isDeadlocked(rendition, character.photos) ? (
              <PhotoAdvice
                character={character}
                rendition={rendition}
                onFixPhotos={() => photosRef.current?.scrollIntoView({ behavior: 'smooth' })}
              />
            ) : undefined
          }
          onGenerate={generate}
          onSelect={(variantId) =>
            rendition && run(charactersApi.selectVariant(rendition.id, variantId))
          }
          onEdit={(variantId, guidance) =>
            rendition
              ? run(
                  charactersApi
                    .edit(rendition.id, variantId, guidance, bookId)
                    .then(() => charactersApi.get(character.id)),
                )
              : Promise.resolve()
          }
          onApprove={() => rendition && run(charactersApi.approve(rendition.id))}
          approveNote={
            character.photos.length > 0 ? t.library.rendition.approveDeletesPhotos : undefined
          }
        />
      </Section>
    </div>
  );
}

function draftFrom(character: CharacterDto): Draft {
  return {
    name: character.name,
    displayName: character.displayName ?? '',
    kind: character.kind,
    role: character.role ?? '',
    ageYears: character.ageYears,
    gender: genderValue(character.gender),
    traits: character.traits.join('\n'),
    favouriteActivities: character.favouriteActivities.join('\n'),
    catchphrase: character.catchphrase ?? '',
  };
}

function toInput(draft: Draft) {
  return {
    name: draft.name.trim(),
    displayName: draft.displayName.trim() || null,
    kind: draft.kind,
    role: draft.role.trim() || null,
    ageYears: draft.ageYears,
    gender: draft.gender || null,
    traits: lines(draft.traits),
    favouriteActivities: lines(draft.favouriteActivities),
    catchphrase: draft.catchphrase.trim() || null,
  };
}

/** Старые свободные значения приводятся к новому закрытому выбору без миграции данных. */
function genderValue(value: string | null): CharacterGenderValue {
  const normalized = value?.trim().toLowerCase().replace(/\s+/gu, ' ') ?? '';
  if (['male', 'man', 'boy', 'мужской', 'мужчина', 'мальчик', 'м'].includes(normalized)) {
    return 'male';
  }
  if (['female', 'woman', 'girl', 'женский', 'женщина', 'девочка', 'ж'].includes(normalized)) {
    return 'female';
  }
  return '';
}

function lines(value: string): string[] {
  return value
    .split(/\n/u)
    .map((item) => item.trim())
    .filter(Boolean);
}

/** Open the optional block when something in it is already filled, so nothing looks lost. */
function hasOptionalDetails(draft: {
  readonly displayName: string;
  readonly role: string;
  readonly favouriteActivities: string;
  readonly catchphrase: string;
}): boolean {
  return Boolean(
    draft.displayName.trim() ||
    draft.role.trim() ||
    draft.favouriteActivities.trim() ||
    draft.catchphrase.trim(),
  );
}
